import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Code2,
  Cpu,
  Layers,
  Server,
  Database,
  ShieldCheck,
  Zap,
  ArrowRight,
  ExternalLink,
  ChevronDown,
  Terminal,
  Activity,
  Lock,
  Workflow,
  Smartphone,
  Globe,
  CheckCircle2,
  RefreshCw,
  GitBranch,
  Settings,
  HardDrive,
  BarChart3,
  Network,
  Check,
  Key,
  Package,
  ArrowDown,
} from "lucide-react";
import { Layout } from "../components/Layout";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/motion";
import { BrowserMock } from "../components/Visuals";
import { openConsultationModal } from "../components/ConsultationModal";
import { SoftwareHeroArchitecture } from "../components/SoftwareHeroArchitecture";
import { SystemArchitectureConsole } from "../components/SystemArchitectureConsole";
import LivingBlueprintArchitecture from "../components/LivingBlueprintArchitecture";
import ProvenImplementations from "../components/ProvenImplementations";
import EngineeringProcess from "../components/EngineeringProcess";
import { LEGAL_ENTITY, PROJECTS } from "../data";



/* =========================================================================
   2. CAPABILITY MATRIX DATA (Section 3 - Light Section)
   ========================================================================= */

const CAPABILITY_DOMAINS = [
  {
    id: "custom-apps",
    number: "01",
    category: "web-mobile",
    title: "Custom Web Applications",
    badge: "Core Engineering",
    icon: Globe,
    tagline: "High-Performance Web Platforms",
    summary:
      "Enterprise web platforms engineered for heavy operational workflows, responsive interaction, and high availability.",
    metric: {
      value: "< 100ms",
      label: "Client Render Latency",
      badge: "Real-World Tested",
    },
    deliverables: [
      {
        icon: Zap,
        title: "Sub-Second Rendering",
        desc: "Edge-rendered UI with optimized bundle loading.",
      },
      {
        icon: ShieldCheck,
        title: "Role-Based Auth",
        desc: "Granular permissions and session integrity.",
      },
      {
        icon: Workflow,
        title: "Enterprise ERP Sync",
        desc: "Real-time bi-directional data exchange pipelines.",
      },
    ],
    tech: ["Next.js", "React", "TypeScript", "Tailwind CSS"],
  },
  {
    id: "mobile-engineering",
    number: "02",
    category: "web-mobile",
    title: "Mobile App Development",
    badge: "iOS & Android",
    icon: Smartphone,
    tagline: "Cross-Platform Native Speed",
    summary:
      "Smooth mobile products engineered for offline reliability, biometric security, and cross-platform performance.",
    metric: {
      value: "60 FPS",
      label: "Fluid UI Frame Rate",
      badge: "Zero Dropped Frames",
    },
    deliverables: [
      {
        icon: Layers,
        title: "Unified Core",
        desc: "Single codebase powering both iOS and Android.",
      },
      {
        icon: RefreshCw,
        title: "Offline-First Sync",
        desc: "Instant local caching with background cloud sync.",
      },
      {
        icon: Cpu,
        title: "Native Hardware",
        desc: "Direct access to biometric auth, camera, and GPS.",
      },
    ],
    tech: ["Flutter", "Dart", "React Native", "SQLite"],
  },
  {
    id: "distributed-apis",
    number: "03",
    category: "backends",
    title: "APIs & Distributed Backends",
    badge: "High Throughput",
    icon: Server,
    tagline: "High-Concurrency Backend Services",
    summary:
      "Microservice architectures and low-latency APIs built for peak traffic surges and sub-50ms execution times.",
    metric: {
      value: "15k+ req/s",
      label: "Sustained Concurrency",
      badge: "Sub-50ms Latency",
    },
    deliverables: [
      {
        icon: Zap,
        title: "Async Worker Queues",
        desc: "Background job execution for resource-heavy tasks.",
      },
      {
        icon: Terminal,
        title: "Type-Safe Contracts",
        desc: "Strict OpenAPI and gRPC schema enforcement.",
      },
      {
        icon: ShieldCheck,
        title: "Traffic Throttling",
        desc: "Distributed rate limits preventing service starvation.",
      },
    ],
    tech: ["NestJS", "FastAPI", "PostgreSQL", "Redis"],
  },
  {
    id: "saas-platforms",
    number: "04",
    category: "cloud-data",
    title: "SaaS Platform Engineering",
    badge: "Multi-Tenant",
    icon: Layers,
    tagline: "Scalable Multi-Tenant Platforms",
    summary:
      "Enterprise SaaS architectures with strict tenant data isolation, automated billing, and workspace management.",
    metric: {
      value: "100%",
      label: "Tenant Data Isolation",
      badge: "Enterprise Compliant",
    },
    deliverables: [
      {
        icon: Lock,
        title: "Isolated Schemas",
        desc: "Cryptographically partitioned data per tenant.",
      },
      {
        icon: Activity,
        title: "Automated Billing",
        desc: "Subscription lifecycle, invoices, and quota metering.",
      },
      {
        icon: BarChart3,
        title: "Tenant Governance",
        desc: "Granular audit logs and workspace administration.",
      },
    ],
    tech: ["Node.js", "PostgreSQL", "Docker", "Stripe"],
  },
  {
    id: "legacy-modernization",
    number: "05",
    category: "backends",
    title: "Legacy Modernization & Refactoring",
    badge: "Architecture Evolution",
    icon: RefreshCw,
    tagline: "Zero-Downtime System Modernization",
    summary:
      "Step-by-step refactoring of monolithic codebases into resilient architectures without operational disruption.",
    metric: {
      value: "0 Downtime",
      label: "Cutover Guarantee",
      badge: "Strangler Pattern",
    },
    deliverables: [
      {
        icon: GitBranch,
        title: "Modular Extraction",
        desc: "Safely isolate and extract legacy modules step by step.",
      },
      {
        icon: CheckCircle2,
        title: "Regression Safeguards",
        desc: "Automated test suites protecting business logic.",
      },
      {
        icon: RefreshCw,
        title: "Instant Fallback",
        desc: "Safe traffic routing with immediate rollback paths.",
      },
    ],
    tech: ["Microservices", "Docker", "PostgreSQL", "CI/CD"],
  },
  {
    id: "database-pipelines",
    number: "06",
    category: "cloud-data",
    title: "Database Architecture & Storage",
    badge: "Data Reliability",
    icon: Database,
    tagline: "Resilient High-Durability Storage",
    summary:
      "ACID-compliant relational architectures, read replicas, and fast Redis caching for instant query execution.",
    metric: {
      value: "< 5ms",
      label: "Cache Read Latency",
      badge: "ACID Compliant",
    },
    deliverables: [
      {
        icon: Zap,
        title: "In-Memory Caching",
        desc: "Sub-millisecond query returns powered by Redis.",
      },
      {
        icon: Network,
        title: "Read Replicas",
        desc: "Intelligent query splitting across read replicas.",
      },
      {
        icon: HardDrive,
        title: "Point-in-Time Recovery",
        desc: "Automated snapshots ensuring zero data loss.",
      },
    ],
    tech: ["PostgreSQL", "Redis", "PgBouncer", "AWS S3"],
  },
];

const CAPABILITY_FILTERS = [
  { id: "all", label: "All Disciplines", count: 6 },
  { id: "web-mobile", label: "Web & Mobile", count: 2 },
  { id: "backends", label: "APIs & Monoliths", count: 2 },
  { id: "cloud-data", label: "SaaS & Database", count: 2 },
];

/* =========================================================================
   EDITORIAL ENGINEERING DISCIPLINE ROW
   Minimalist, typographic row with thin hairline dividers, generous spacing,
   subtle orange hover trace line, title emphasis, and directional arrow shift.
   Replaces the dashboard card grid with a quiet, confident consulting format.
   ========================================================================= */

const ENGINEERING_DISCIPLINES = [
  {
    id: "custom-apps",
    number: "01",
    title: "Custom Web Applications",
    description:
      "Business applications designed around your workflows and operational needs.",
  },
  {
    id: "mobile-engineering",
    number: "02",
    title: "Mobile App Development",
    description:
      "Mobile products for connected, responsive, and reliable user experiences.",
  },
  {
    id: "distributed-apis",
    number: "03",
    title: "APIs & Distributed Backends",
    description:
      "Backend services and integrations that connect applications and business systems.",
  },
  {
    id: "saas-platforms",
    number: "04",
    title: "SaaS Platform Engineering",
    description:
      "Multi-tenant software products with scalable business and subscription workflows.",
  },
  {
    id: "legacy-modernization",
    number: "05",
    title: "Legacy Modernization & Refactoring",
    description:
      "Improve existing systems through structured refactoring and modern architecture.",
  },
  {
    id: "database-pipelines",
    number: "06",
    title: "Database Architecture & Storage",
    description:
      "Reliable data architecture designed around consistency, performance, and maintainability.",
  },
];

function EditorialDisciplineRow({
  discipline,
  isActive,
  onHover,
  onLeave,
  onScope,
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onScope}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      onFocus={onHover}
      onBlur={onLeave}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onScope();
        }
      }}
      className={`group relative py-3.5 sm:py-4.5 border-t border-neutral-300/80 transition-all duration-200 cursor-pointer outline-none ${
        isActive ? "bg-white/40" : "hover:bg-white/20"
      }`}
    >
      {/* Subtle fine orange indicator trace line */}
      <div
        className={`absolute top-0 left-0 h-[2px] bg-[#FF5A1F] transition-all duration-300 ease-out ${
          isActive
            ? "w-full opacity-100"
            : "w-0 opacity-0 group-hover:w-20 group-hover:opacity-100"
        }`}
      />

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-6">
        {/* Left Side: Number + Title */}
        <div className="flex items-baseline gap-3 sm:gap-4 flex-shrink-0 md:w-5/12">
          <span
            className={`font-mono text-xs font-semibold transition-colors duration-200 ${
              isActive
                ? "text-[#FF5A1F]"
                : "text-neutral-500 group-hover:text-[#FF5A1F]"
            }`}
          >
            {discipline.number}
          </span>
          <h3
            className={`font-display text-base sm:text-lg lg:text-[19px] font-bold tracking-tight text-[#0B0B0B] transition-colors duration-200 ${
              isActive ? "text-[#FF5A1F]" : "group-hover:text-[#0B0B0B]"
            }`}
          >
            {discipline.title}
          </h3>
        </div>

        {/* Center / Right Side: Concise Description + Action Arrow */}
        <div className="flex items-center justify-between gap-4 md:w-7/12">
          <p
            className={`text-xs sm:text-[13px] md:text-sm font-sans leading-relaxed transition-colors duration-200 ${
              isActive
                ? "text-[#0B0B0B]"
                : "text-neutral-600 group-hover:text-neutral-900"
            }`}
          >
            {discipline.description}
          </p>

          <div className="flex items-center gap-2 flex-shrink-0 text-xs font-semibold font-mono text-neutral-500 group-hover:text-[#FF5A1F] transition-colors">
            <span className="hidden lg:inline opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-[10px] tracking-wider uppercase">
              Scope
            </span>
            <span className="w-7 h-7 rounded-full border border-neutral-300/80 group-hover:border-[#FF5A1F] grid place-items-center transition-all duration-200 group-hover:bg-[#FF5A1F] group-hover:text-white">
              <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform duration-200" />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}



/* =========================================================================
   4. ENGINEERING DELIVERY LIFECYCLE DATA (Section 5 - Light Section)
   ========================================================================= */

const DELIVERY_STAGES = [
  {
    step: "01",
    name: "Discovery & Architecture",
    tag: "Phase 1: Foundations",
    summary:
      "We unpack your business requirements, define domain boundaries, design database schemas, and create technical contracts before writing code.",
    deliverables: [
      "Architecture Decision Records (ADRs)",
      "Entity-Relationship Diagrams (ERD)",
      "OpenAPI service interface definitions",
      "Security threat model and risk assessment",
    ],
  },
  {
    step: "02",
    name: "Iterative Sprint Development",
    tag: "Phase 2: Build",
    summary:
      "Two-week development sprints structured around test-driven development, modular codebases, strict type checking, and peer pull-request reviews.",
    deliverables: [
      "Continuous integration validation per pull request",
      "Staging preview environments for stakeholder review",
      "Automated unit and integration test suites",
      "Clean TypeScript and Python typed codebases",
    ],
  },
  {
    step: "03",
    name: "Validation & Security Hardening",
    tag: "Phase 3: Verify",
    summary:
      "Rigorous pre-launch validation including concurrency stress testing, database query analysis, zero-trust vulnerability scanning, and cross-browser checks.",
    deliverables: [
      "Concurrency and load testing benchmark reports",
      "Zero-trust security vulnerability verification",
      "Cross-platform responsive validation",
      "Database slow-query profiling and indexing",
    ],
  },
  {
    step: "04",
    name: "Production Release & SRE",
    tag: "Phase 4: Operate",
    summary:
      "Zero-downtime production deployment supported by automated CI/CD pipelines, container monitoring, automated database migrations, and operational runbooks.",
    deliverables: [
      "Automated rolling production deployments",
      "Prometheus and SRE health telemetry dashboards",
      "Disaster recovery and automated backup runbooks",
      "Post-launch technical support coverage",
    ],
  },
];

/* =========================================================================
   5. TECHNICAL FAQs (Section 6 - Dark Section)
   ========================================================================= */

const SOFTWARE_FAQS = [
  {
    q: "How does Neuravolt manage intellectual property and source code ownership?",
    a: "Your organization retains 100% full intellectual property ownership of all custom software code, database schemas, architectural specifications, and associated assets created for your project. Upon completion and milestone sign-off, full repository control and production deployment keys are handed over directly to your team.",
  },
  {
    q: "What criteria guide your system architecture and tech stack decisions?",
    a: "We choose technologies based strictly on your performance requirements, anticipated transaction concurrency, data integrity needs, and long-term maintenance costs. Rather than following transient trends, we prioritize proven, high-performance technologies such as React, Next.js, Flutter, NestJS, FastAPI, and PostgreSQL.",
  },
  {
    q: "Can you modernize an existing legacy software system without a full rewrite?",
    a: "Yes. We frequently use the Strangler Fig architectural pattern to modernize legacy systems incrementally. This approach allows us to decouple and modernize individual business services one by one, verifying each component in production while keeping your primary business operations running without interruption.",
  },
  {
    q: "How do you guarantee data security, access control, and regulatory compliance?",
    a: "Every architecture implements zero-trust security standards from day one. This includes end-to-end data encryption in transit via TLS 1.3, encrypted storage at rest (AES-256), strict role-based access control (RBAC), parameterized database queries to eliminate injection vulnerabilities, and alignment with DPDP and GDPR standards.",
  },
  {
    q: "What maintenance and SLA support options are available after deployment?",
    a: "We offer structured software maintenance agreements covering proactive server health monitoring, security patch management, framework updates, database backup verification, and defined emergency incident response times to protect continuous system availability.",
  },
];

/* =========================================================================
   MAIN SOFTWARE DEVELOPMENT PAGE COMPONENT
   ========================================================================= */

export default function SoftwareDevelopment() {
  // Section 3: Hovered discipline for bidirectional blueprint sync
  const [hoveredListDisciplineId, setHoveredListDisciplineId] = useState(null);

  // Section 6: FAQ Accordion state
  const [openFaq, setOpenFaq] = useState(0);

  // Structured Data Schema
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Software Development Services",
    description:
      "Enterprise software engineering, custom web applications, cross-platform mobile apps, and distributed backend architectures by Neuravolt.",
    url: "https://neuravolt.in/services/software-development",
    provider: {
      "@type": "Organization",
      name: "Neuravolt Technology",
      legalName: LEGAL_ENTITY,
      url: "https://neuravolt.in",
    },
    areaServed: { "@type": "Country", name: "India" },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Software Development Capabilities",
      itemListElement: CAPABILITY_DOMAINS.map((c) => ({
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: c.title },
      })),
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: SOFTWARE_FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.a,
      },
    })),
  };

  return (
    <Layout>
      <Seo
        title="Custom Software Development Company | Enterprise Software Engineering | NeuraVolt"
        description="Partner with NeuraVolt for bespoke enterprise software engineering, scalable cloud platforms, and tailored business workflows. Serving enterprises across India, Gwalior & globally."
        keywords="custom software development company, custom software development, software development company, enterprise software development, custom software engineering, software development company in Gwalior, bespoke software development company, NeuraVolt"
        path="/services/software-development"
        jsonLd={[serviceSchema, faqSchema]}
      />

      {/* ===================================================================
          SECTION 1: LIGHT ANIMATED ARCHITECTURE HERO
          Visual Direction: Matching exact reference image layout & proportions
          Background: Warm off-white #FAF8F5 with faint isometric grid
          =================================================================== */}
      <section className="relative bg-[#FAF8F5] text-[#0B0B0B] pt-28 pb-16 lg:pt-36 lg:pb-24 overflow-hidden border-b border-neutral-200/60">
        {/* Subtle Ambient Warm Glow */}
        <div
          className="absolute top-1/4 right-1/4 w-[520px] h-[520px] rounded-full pointer-events-none opacity-20"
          style={{
            background:
              "radial-gradient(circle, rgba(255, 90, 31, 0.3) 0%, rgba(250, 248, 245, 0) 70%)",
            filter: "blur(70px)",
          }}
        />

        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
            {/* Left Column: Headline, Copy, Actions & Proof Points */}
            <div className="lg:col-span-5 xl:col-span-5 z-10">
              {/* Eyebrow Label with Brand Orange Dot */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-neutral-200/50 border border-neutral-300/60 text-xs font-mono uppercase tracking-widest text-neutral-600 mb-6"
              >
                <span className="w-2 h-2 rounded-full bg-[#FF5A1F]" />
                ENTERPRISE SOFTWARE ENGINEERING
              </motion.div>

              {/* Headline */}
              <motion.h1
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="font-display font-heading text-4xl sm:text-5xl lg:text-[56px] xl:text-[62px] font-bold tracking-tight text-[#0B0B0B] leading-[1.08] mb-6"
              >
                Mission-Critical<br className="hidden sm:inline" />
                Systems, <span className="text-[#FF5A1F]">Engineered</span><br className="hidden sm:inline" />
                <span className="text-[#FF5A1F]">for Scale.</span>
              </motion.h1>

              {/* Supporting Copy */}
              <motion.p
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.2 }}
                className="text-base sm:text-lg text-neutral-600 leading-relaxed max-w-lg font-sans mb-8"
              >
                We design, build, and operate resilient software products tailored
                to your business.
              </motion.p>

              {/* Dual Action Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.3 }}
                className="flex flex-wrap items-center gap-3.5 mb-10"
              >
                <button
                  onClick={() =>
                    openConsultationModal("Software Development Project Scope")
                  }
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#FF5A1F] text-white font-medium text-sm hover:bg-[#ff6d38] transition-all shadow-[0_12px_24px_-6px_rgba(255,90,31,0.35)] cursor-pointer group"
                >
                  Start a Project
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>

                <a
                  href="#proven-implementations"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-[#0B0B0B] font-medium text-sm border border-neutral-200/90 hover:bg-neutral-50 hover:border-neutral-300 transition-all shadow-sm"
                >
                  <Package className="w-4 h-4 text-neutral-700" />
                  Explore Our Work
                </a>
              </motion.div>

              {/* Verified Proof Points Row */}
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.4 }}
                className="pt-6 border-t border-neutral-200/80 grid grid-cols-3 gap-4 max-w-lg"
              >
                <div className="pr-3 border-r border-neutral-200/80">
                  <div className="font-display text-2xl sm:text-3xl font-bold text-[#0B0B0B]">
                    99.98%
                  </div>
                  <div className="text-[10px] font-mono tracking-wider text-neutral-600 uppercase mt-1">
                    UPTIME
                  </div>
                </div>

                <div className="pr-3 border-r border-neutral-200/80">
                  <div className="font-display text-2xl sm:text-3xl font-bold text-[#0B0B0B]">
                    &lt; 24ms
                  </div>
                  <div className="text-[10px] font-mono tracking-wider text-neutral-600 uppercase mt-1">
                    RESPONSE TIME
                  </div>
                </div>

                <div>
                  <div className="font-display text-2xl sm:text-3xl font-bold text-[#0B0B0B]">
                    Zero-Trust
                  </div>
                  <div className="text-[10px] font-mono tracking-wider text-neutral-600 uppercase mt-1">
                    SECURITY
                  </div>
                </div>
              </motion.div>

              {/* Scroll To Explore Indicator */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="mt-8 pt-2"
              >
                <a
                  href="#architecture-topology"
                  className="inline-flex items-center gap-2 text-xs font-mono tracking-widest uppercase text-neutral-600 hover:text-[#0B0B0B] transition-colors group"
                >
                  <span className="w-6 h-6 rounded-full border border-neutral-300 grid place-items-center group-hover:border-neutral-600 transition-colors">
                    <ArrowDown className="w-3 h-3 text-neutral-600 group-hover:translate-y-0.5 transition-transform" />
                  </span>
                  SCROLL TO EXPLORE
                </a>
              </motion.div>
            </div>

            {/* Right Column: Signature Isometric Architecture Visualization */}
            <div className="lg:col-span-7 xl:col-span-7 relative">
              <SoftwareHeroArchitecture />
            </div>
          </div>
        </div>
      </section>

      {/* ===================================================================
          SECTION 2: DARK ARCHITECTURE TOPOLOGY & SPECIFICATION
          Background: Deep near-black #0B0B0B (section-dark)
          =================================================================== */}
      <SystemArchitectureConsole />


      {/* ===================================================================
          SECTION 3: LIGHT CAPABILITY MATRIX (INTERACTIVE BENTO GRID)
          Background: Warm off-white #F7F6F2 (mesh-light)
          =================================================================== */}
      <section id="engineering-capabilities" className="py-24 lg:py-32 bg-[#F7F6F2] text-[#0B0B0B] relative overflow-hidden">
        {/* Subtle dot matrix texture */}
        <div
          className="absolute inset-0 opacity-[0.035] pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(#000000 1.2px, transparent 1.2px)",
            backgroundSize: "28px 28px",
          }}
        />

        <div className="max-w-7xl mx-auto px-5 sm:px-8 relative z-10">
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Reveal>
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-neutral-200/90 shadow-2xs text-xs font-mono uppercase tracking-widest text-neutral-600 mb-4">
                <span className="w-2 h-2 rounded-full bg-[#FF5A1F] animate-pulse" />
                Engineering Capabilities
              </div>
            </Reveal>

            <Reveal delay={0.06}>
              <h2 className="font-display font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0B0B0B] mb-4 leading-tight">
                From business challenge to engineered solution.
              </h2>
            </Reveal>

            <Reveal delay={0.12}>
              <p className="text-base sm:text-lg text-neutral-600 leading-relaxed font-sans max-w-2xl mx-auto">
                Six disciplines. Clear capabilities. One engineering partner.
              </p>
            </Reveal>
          </div>

          {/* ===============================================================
              CINEMATIC LIVING BLUEPRINT ANIMATION & ARCHITECTURE MAPPING
              Real SVG + HTML + CSS interactive topology with one-time entrance
              =============================================================== */}
          <div className="mb-12">
            <LivingBlueprintArchitecture
              externalActiveDisciplineId={hoveredListDisciplineId}
              onScopeDiscipline={(disc) => {
                openConsultationModal(`Scoping: ${disc.title}`);
              }}
            />
          </div>

          {/* ===============================================================
              EDITORIAL TYPOGRAPHIC DISCIPLINES LIST
              Minimalist, compact, high-contrast rows
              =============================================================== */}
          <div className="max-w-4xl mx-auto">
            <div className="border-b border-neutral-300/80">
              {ENGINEERING_DISCIPLINES.map((discipline) => (
                <EditorialDisciplineRow
                  key={discipline.id}
                  discipline={discipline}
                  isActive={hoveredListDisciplineId === discipline.id}
                  onHover={() => setHoveredListDisciplineId(discipline.id)}
                  onLeave={() => setHoveredListDisciplineId(null)}
                  onScope={() =>
                    openConsultationModal(`Scoping: ${discipline.title}`)
                  }
                />
              ))}
            </div>

            {/* Clean Architectural Next Step Strip */}
            <div className="mt-8 pt-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-xs font-mono text-neutral-600">
              <span className="tracking-wider uppercase">
                Need an integrated multi-discipline architecture?
              </span>
              <button
                onClick={() =>
                  openConsultationModal("Full-Lifecycle Architecture Scoping")
                }
                className="inline-flex items-center gap-2 text-xs font-bold text-[#0B0B0B] hover:text-[#FF5A1F] transition-colors cursor-pointer group"
              >
                <span>Schedule Architecture Scoping</span>
                <ArrowRight className="w-3.5 h-3.5 text-[#FF5A1F] group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ===================================================================
          SECTION 4: DARK VERIFIED PROJECT CASE STUDIES (PROVEN IMPLEMENTATIONS)
          Spacious editorial layout matching senior-level consulting specs
          =================================================================== */}
      <ProvenImplementations
        onOpenConsultation={(source) => openConsultationModal(source)}
      />

      {/* ===================================================================
          SECTION 5: INTERACTIVE 4-STAGE ENGINEERING PROCESS (LIGHT SECTION)
          Interactive motion bento cards with live micro-graphics & 3D tilt
          =================================================================== */}
      <EngineeringProcess
        onOpenConsultation={(source) => openConsultationModal(source)}
      />

      {/* ===================================================================
          SECTION 6: DARK TECHNICAL FAQ & FINAL CONVERSION CALLOUT
          Background: Deep near-black #0B0B0B (section-dark)
          =================================================================== */}
      <section className="py-24 lg:py-32 bg-[#0B0B0B] text-white relative section-dark">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-16">
            <Reveal>
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-mono uppercase tracking-widest text-neutral-300 mb-4">
                <span className="w-2 h-2 rounded-full bg-[#FF5A1F]" />
                Technical Questions
              </div>
            </Reveal>

            <Reveal delay={0.06}>
              <h2 className="font-display font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-5">
                Questions and Answers.
              </h2>
            </Reveal>

            <Reveal delay={0.12}>
              <p className="text-base sm:text-lg text-neutral-300 font-sans max-w-2xl mx-auto">
                Straightforward answers to key questions about source code
                ownership, architectural methodology, security, and ongoing SLAs.
              </p>
            </Reveal>
          </div>

          {/* Compact Accordion */}
          <div className="space-y-3.5 mb-20">
            {SOFTWARE_FAQS.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div
                  key={idx}
                  className="rounded-2xl border border-white/10 bg-[#171719] overflow-hidden transition-all"
                >
                  <button
                    onClick={() => setOpenFaq(isOpen ? -1 : idx)}
                    className="w-full text-left p-5 sm:p-6 flex items-center justify-between gap-4 cursor-pointer"
                  >
                    <span className="font-display font-bold text-base sm:text-lg text-white">
                      {faq.q}
                    </span>
                    <span
                      className={`w-8 h-8 rounded-full grid place-items-center flex-shrink-0 transition-transform ${
                        isOpen
                          ? "bg-[#FF5A1F] text-white rotate-180"
                          : "bg-white/10 text-neutral-300"
                      }`}
                    >
                      <ChevronDown className="w-4 h-4" />
                    </span>
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="px-5 sm:px-6 pb-6 pt-1 text-sm sm:text-base text-neutral-300 leading-relaxed font-sans border-t border-white/10">
                          {faq.a}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>

          {/* Final Conversion Card */}
          <Reveal>
            <div className="rounded-3xl bg-[#171719] text-white p-8 sm:p-12 border border-white/10 relative overflow-hidden text-center sm:text-left shadow-2xl">
              {/* Subtle ambient orange illumination */}
              <div
                className="absolute -right-20 -bottom-20 w-80 h-80 rounded-full pointer-events-none opacity-20"
                style={{
                  background:
                    "radial-gradient(circle, rgba(255, 90, 31, 0.6) 0%, rgba(23, 23, 25, 0) 70%)",
                  filter: "blur(40px)",
                }}
              />

              <div className="relative z-10 flex flex-col sm:flex-row items-center justify-between gap-8">
                <div className="max-w-xl">
                  <span className="text-xs font-mono uppercase tracking-widest text-[#FF5A1F] block mb-2 font-semibold">
                    Start a Technical Scoping Session
                  </span>
                  <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 leading-tight">
                    Have a Complex Software Challenge?
                  </h3>
                  <p className="text-sm sm:text-base text-neutral-300 font-sans leading-relaxed">
                    Speak directly with our senior engineering team. We evaluate
                    your system requirements, identify architectural risks, and
                    deliver a clear technical roadmap.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
                  <button
                    onClick={() =>
                      openConsultationModal("Software Architecture Scoping")
                    }
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#FF5A1F] text-white text-sm font-semibold hover:bg-[#ff6d38] transition-colors shadow-lg shadow-[#FF5A1F]/20 cursor-pointer whitespace-nowrap"
                  >
                    Discuss Your Architecture
                    <ArrowRight className="w-4 h-4" />
                  </button>

                  <Link
                    to="/portfolio"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-white/5 hover:bg-white/10 text-white text-sm font-medium border border-white/10 transition-colors whitespace-nowrap"
                  >
                    Explore Portfolio
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
}
